import {
  SkillConfigurationErrorCode,
  SkillLevelType,
  SkillPracticeTaskType,
  SkillPracticeTaskStatus,
  SkillScoreStatus,
  SkillSpecializationFillTaskStatus,
  SkillSpecializationFolderId,
  SkillSpecializationStatus,
  SkillTestViewState,
  SkillTrainingRequirementScope,
  SKILL_ERROR_CONFIGURATION_CONFLICT,
  SKILL_ERROR_DEACTIVATION_ACTIVE_TASK,
  SKILL_ERROR_DEACTIVATION_REQUIREMENT_ACTIVE,
  SKILL_ERROR_LEVEL_NOT_AVAILABLE,
  SKILL_ERROR_REQUIREMENT_UNPUBLISHED,
  SKILL_ERROR_STRUCTURE_ACTIVE_TASK,
  SKILL_ERROR_STRUCTURE_INVALID,
  SKILL_ERROR_STRUCTURE_REQUIRED,
  SKILL_LEVEL_TYPE_TASK,
  SKILL_LEVEL_TYPE_TEST,
  SKILL_REQUIREMENT_COMPANY,
  SKILL_REQUIREMENT_DEPARTMENT,
  SKILL_REQUIREMENT_FUTURE_HEAD,
  SKILL_REQUIREMENT_CURRENT_HEAD,
  SKILL_TEST_STATE_ERROR,
  SKILL_TEST_STATE_GENERATING,
  SKILL_TEST_STATE_IDLE,
  SKILL_TEST_STATE_RESULT,
  SKILL_TEST_STATE_TESTING,
} from '../models/skill-learning.model';

export enum SkillLearningLevelTab {
  LEVEL_1,
  LEVEL_2,
  LEVEL_3,
}

export const SKILL_LEARNING_LEVEL_TAB = SkillLearningLevelTab;

export const SKILL_LEARNING_LEVEL_COUNT = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
};

export const SKILL_LEVEL_TYPE = {
  TEST: SKILL_LEVEL_TYPE_TEST as SkillLevelType,
  TASK: SKILL_LEVEL_TYPE_TASK as SkillLevelType,
};

export const SKILL_LEVEL_TYPE_LABEL = {
  TEST: 'SKILL_LEARNING_LEVEL_TYPE_TEST',
  TASK: 'SKILL_LEARNING_LEVEL_TYPE_TASK',
};

export const SKILL_REQUIREMENT_SCOPE = {
  CURRENT_HEAD: SKILL_REQUIREMENT_CURRENT_HEAD as SkillTrainingRequirementScope,
  COMPANY_PROBATION: SKILL_REQUIREMENT_COMPANY as SkillTrainingRequirementScope,
  DEPARTMENT_PROBATION: SKILL_REQUIREMENT_DEPARTMENT as SkillTrainingRequirementScope,
  FUTURE_HEAD: SKILL_REQUIREMENT_FUTURE_HEAD as SkillTrainingRequirementScope,
};

export const SKILL_CONFIGURATION_ERROR = {
  STRUCTURE_INVALID: SKILL_ERROR_STRUCTURE_INVALID as SkillConfigurationErrorCode,
  CONFLICT: SKILL_ERROR_CONFIGURATION_CONFLICT as SkillConfigurationErrorCode,
  REQUIRED_LEVEL: SKILL_ERROR_STRUCTURE_REQUIRED as SkillConfigurationErrorCode,
  ACTIVE_TASK: SKILL_ERROR_STRUCTURE_ACTIVE_TASK as SkillConfigurationErrorCode,
  DEACTIVATION_REQUIREMENT_ACTIVE: SKILL_ERROR_DEACTIVATION_REQUIREMENT_ACTIVE as SkillConfigurationErrorCode,
  DEACTIVATION_ACTIVE_TASK: SKILL_ERROR_DEACTIVATION_ACTIVE_TASK as SkillConfigurationErrorCode,
  REQUIREMENT_UNPUBLISHED: SKILL_ERROR_REQUIREMENT_UNPUBLISHED as SkillConfigurationErrorCode,
  LEVEL_NOT_AVAILABLE: SKILL_ERROR_LEVEL_NOT_AVAILABLE as SkillConfigurationErrorCode,
};

export const SKILL_PRACTICE_TASK_TYPE = {
  PRACTICE: 'PRACTICE' as SkillPracticeTaskType,
  TEST_REVIEW: 'TEST_REVIEW' as SkillPracticeTaskType,
};

export const SKILL_SPECIALIZATION_STATUS = {
  DRAFT: 'DRAFT' as SkillSpecializationStatus,
  FILLING_IN_PROGRESS: 'FILLING_IN_PROGRESS' as SkillSpecializationStatus,
  READY_FOR_LEARNING: 'READY_FOR_LEARNING' as SkillSpecializationStatus,
};

export const SKILL_DEPARTMENT_QUERY_PARAM = 'departmentId';
export const SKILL_URL_QUERY_SEPARATOR = '?';

export const SKILL_SPECIALIZATION_FOLDER = {
  OWN: 'OWN' as SkillSpecializationFolderId,
  OTHER: 'OTHER' as SkillSpecializationFolderId,
  CROSS: 'CROSS' as SkillSpecializationFolderId,
  COMPANY: 'COMPANY' as SkillSpecializationFolderId,
};

export const SKILL_SPECIALIZATION_FOLDER_ORDER: SkillSpecializationFolderId[] = [
  SKILL_SPECIALIZATION_FOLDER.OWN,
  SKILL_SPECIALIZATION_FOLDER.OTHER,
  SKILL_SPECIALIZATION_FOLDER.CROSS,
  SKILL_SPECIALIZATION_FOLDER.COMPANY,
];

export const SKILL_SPECIALIZATION_FOLDER_LABEL: {
  [folderId in SkillSpecializationFolderId]: string;
} = {
  OWN: 'SKILL_LEARNING_FOLDER_OWN',
  OTHER: 'SKILL_LEARNING_FOLDER_OTHER',
  CROSS: 'SKILL_LEARNING_FOLDER_CROSS',
  COMPANY: 'SKILL_LEARNING_FOLDER_COMPANY',
};

export const SKILL_SPECIALIZATION_DEFAULT_FOLDER = SKILL_SPECIALIZATION_FOLDER.OWN;

export const SKILL_LEARNING_FIRST_INDEX = 0;
export const SKILL_LEARNING_LAST_INDEX_OFFSET = 1;
export const SKILL_LEARNING_EMPTY_TEXT = '';
export const SKILL_LEARNING_EMPTY_COUNT = 0;
export const SKILL_LEARNING_NOT_FOUND_INDEX = -1;

export const SKILL_VALUE_TYPE = {
  NUMBER: 'number',
};

export const SKILL_HTTP_STATUS = {
  UNPROCESSABLE_ENTITY: 422,
  FORBIDDEN: 403,
  CONFLICT: 409,
  PRECONDITION_FAILED: 412,
  TOO_MANY_REQUESTS: 429,
  SERVICE_UNAVAILABLE: 503,
};

export const SKILL_LEARNING_ROUTE = {
  SPECIALIZATIONS: '/dashboard/specializations',
  LEARNING: '/dashboard/learning',
};

export const SKILL_SPECIALIZATION_ROUTE_PARAM = 'specializationId';

export const SKILL_LEARNING_ROUTE_PART = {
  CREATE: 'create',
  EDIT: 'edit',
};

export const SKILL_LEARNING_SIDEBAR = {
  SPECIALIZATIONS: 'spec-list',
};

export const SKILL_LEARNING_EDIT_PAGE = {
  GENERAL: 'general' as 'general',
  LEVELS: 'levels' as 'levels',
};

export type EditSpecializationPage =
  typeof SKILL_LEARNING_EDIT_PAGE.GENERAL
  | typeof SKILL_LEARNING_EDIT_PAGE.LEVELS;

export const SKILL_SPECIALIZATION_FILL_TASK_STATUS = {
  CREATING: 'CREATING' as SkillSpecializationFillTaskStatus,
  CREATED: 'CREATED' as SkillSpecializationFillTaskStatus,
  IN_WORK: 'IN_WORK' as SkillSpecializationFillTaskStatus,
  READY_FOR_REVIEW: 'READY_FOR_REVIEW' as SkillSpecializationFillTaskStatus,
  COMPLETED: 'COMPLETED' as SkillSpecializationFillTaskStatus,
  CANCELLED: 'CANCELLED' as SkillSpecializationFillTaskStatus,
  COMPLETED_INVALID: 'COMPLETED_INVALID' as SkillSpecializationFillTaskStatus,
};

export const SKILL_ACTIVE_FILL_TASK_STATUSES: SkillSpecializationFillTaskStatus[] = [
  SKILL_SPECIALIZATION_FILL_TASK_STATUS.CREATING,
  SKILL_SPECIALIZATION_FILL_TASK_STATUS.CREATED,
  SKILL_SPECIALIZATION_FILL_TASK_STATUS.IN_WORK,
  SKILL_SPECIALIZATION_FILL_TASK_STATUS.READY_FOR_REVIEW,
];

export const SKILL_LEARNING_SESSION_MESSAGE = {
  LEVEL_ONE_COMPLETED: 'SKILL_LEARNING_SESSION_LEVEL1_COMPLETED_TEXT',
  LEVEL_ONE_ONLY_COMPLETED: 'SKILL_LEARNING_SESSION_LEVEL1_ONLY_COMPLETED_TEXT',
};

export const SKILL_LEARNER_ACTION_MESSAGE = {
  LEVEL_NOT_AVAILABLE: 'SKILL_LEARNING_LEVEL_NOT_AVAILABLE',
  REJECTED: 'SKILL_LEARNING_LEVEL_ACTION_REJECTED',
};

export const SKILL_TEST_VIEW_STATE = {
  IDLE: SKILL_TEST_STATE_IDLE as SkillTestViewState,
  GENERATING: SKILL_TEST_STATE_GENERATING as SkillTestViewState,
  TESTING: SKILL_TEST_STATE_TESTING as SkillTestViewState,
  RESULT: SKILL_TEST_STATE_RESULT as SkillTestViewState,
  ERROR: SKILL_TEST_STATE_ERROR as SkillTestViewState,
};

export const SKILL_TEST_TIME = {
  MINIMUM_COOLDOWN_MINUTES: 1,
  SECONDS_PER_MINUTE: 60,
  MINUTES_PER_HOUR: 60,
  MILLISECONDS_PER_SECOND: 1000,
};

export const SKILL_TEST_MESSAGE = {
  SUBMISSION_PENDING: 'SKILL_LEARNING_TEST_SUBMISSION_PENDING',
  LEVEL_NOT_AVAILABLE: 'SKILL_LEARNING_LEVEL_NOT_AVAILABLE',
  BANK_NOT_READY: 'SKILL_LEARNING_TEST_BANK_NOT_READY',
  SERVICE_UNAVAILABLE: 'SKILL_LEARNING_TEST_ERROR_SERVICE_UNAVAILABLE',
  GENERATION_ERROR: 'SKILL_LEARNING_ADMIN_THEORY_TEST_BANK_GENERATION_ERROR',
  GENERIC_ERROR: 'SKILL_LEARNING_TEST_ERROR_GENERIC',
  RETRY_IN_HOURS: 'SKILL_LEARNING_TEST_RETRY_IN_HOURS',
  RETRY_IN_MINUTES: 'SKILL_LEARNING_TEST_RETRY_IN_MINUTES',
  DAILY_LIMIT_REACHED: 'SKILL_LEARNING_TEST_DAILY_LIMIT_REACHED',
  REVIEW_ALREADY_PASSED: 'SKILL_LEARNING_TEST_REVIEW_ALREADY_PASSED',
  RETRY_RULE_HINT: 'SKILL_LEARNING_TEST_RETRY_RULE_HINT',
};

export const SKILL_TEST_ERROR_TOKEN = {
  AI_SERVICE_UNAVAILABLE: 'AI_SERVICE_UNAVAILABLE',
  CURATOR_REVIEW: 'curator review',
  REVIEW_ALREADY_PASSED: 'test_review_already_passed',
  ALREADY_COMPLETED_WITH_CURATOR: 'already completed with curator',
  BANK_NOT_READY: 'test_bank_not_ready',
};

export const SKILL_LEARNING_EDIT_MESSAGE = {
  SPECIALIZATION_UPDATED: 'SKILL_LEARNING_ADMIN_EDIT_SPECIALIZATION_UPDATED',
  CURATORS_UPDATED: 'SKILL_LEARNING_ADMIN_EDIT_CURATORS_UPDATED',
  LEVEL_SAVED: 'SKILL_LEARNING_ADMIN_EDIT_LEVEL_SAVED',
  CONTENT_CONFLICT: 'SKILL_LEARNING_ADMIN_EDIT_CONTENT_CONFLICT_ERROR',
  DOCUMENT_UPLOAD_FAILED: 'SKILL_LEARNING_ADMIN_EDIT_LEVEL_DOCUMENT_UPLOAD_ERROR',
  DOCUMENT_REFRESH_FAILED: 'SKILL_LEARNING_ADMIN_EDIT_LEVEL_DOCUMENT_REFRESH_ERROR',
};

export const SKILL_REQUIREMENT_SCOPE_LABEL = {
  CURRENT_HEAD: 'SKILL_LEARNING_REQUIREMENTS_CURRENT_HEAD',
  COMPANY_PROBATION: 'SKILL_LEARNING_REQUIREMENTS_COMPANY',
  DEPARTMENT_PROBATION: 'SKILL_LEARNING_REQUIREMENTS_DEPARTMENT',
  FUTURE_HEAD: 'SKILL_LEARNING_REQUIREMENTS_FUTURE_HEAD',
};

export const SKILL_FILL_TASK_STATUS_LABEL_PREFIX = 'SKILL_LEARNING_ADMIN_FILL_TASK_STATUS_';
export const SKILL_SPECIALIZATION_STATUS_LABEL_PREFIX = 'SKILL_LEARNING_ADMIN_STATUS_';

export const SKILL_STRUCTURE_MESSAGE = {
  UPDATED: 'SKILL_LEARNING_ADMIN_STRUCTURE_UPDATED',
  REMOVE_CONFIRM: 'SKILL_LEARNING_ADMIN_STRUCTURE_REMOVE_CONFIRM',
  TYPE_CONFIRM: 'SKILL_LEARNING_ADMIN_STRUCTURE_TYPE_CONFIRM',
  FORCE_LEVEL_THREE_CONFIRM: 'SKILL_LEARNING_ADMIN_STRUCTURE_FORCE_LEVEL3_CONFIRM',
  CONFLICT: 'SKILL_LEARNING_ADMIN_STRUCTURE_CONFLICT_ERROR',
  REQUIRED_LEVEL: 'SKILL_LEARNING_ADMIN_STRUCTURE_REQUIRED_LEVEL_ERROR',
  ACTIVE_TASK: 'SKILL_LEARNING_ADMIN_STRUCTURE_ACTIVE_TASK_ERROR',
  DENIED: 'SKILL_LEARNING_ADMIN_STRUCTURE_DENIED_ERROR',
  CONTEXT_DENIED: 'SKILL_LEARNING_ADMIN_STRUCTURE_CONTEXT_DENIED',
  REVISION_REQUIRED: 'SKILL_LEARNING_ADMIN_STRUCTURE_REVISION_REQUIRED',
  INVALID: 'SKILL_LEARNING_ADMIN_STRUCTURE_INVALID_ERROR',
};

export const SKILL_REQUIREMENT_MESSAGE = {
  UPDATED: 'SKILL_LEARNING_REQUIREMENTS_UPDATED',
  DISCARD_DEPARTMENT: 'SKILL_LEARNING_REQUIREMENTS_DISCARD_DEPARTMENT_CONFIRM',
  CONFLICT: 'SKILL_LEARNING_REQUIREMENTS_CONFLICT_ERROR',
  UNPUBLISHED: 'SKILL_LEARNING_REQUIREMENTS_UNPUBLISHED',
  DENIED: 'SKILL_LEARNING_REQUIREMENTS_DENIED_ERROR',
  SAVE_ERROR: 'SKILL_LEARNING_REQUIREMENTS_SAVE_ERROR',
};

export const SKILL_DEACTIVATION_MESSAGE = {
  CONFLICT: 'SKILL_LEARNING_ADMIN_DEACTIVATION_CONFLICT_ERROR',
  REQUIREMENT_ACTIVE: 'SKILL_LEARNING_ADMIN_DEACTIVATION_REQUIREMENT_ACTIVE_ERROR',
  ACTIVE_TASK: 'SKILL_LEARNING_ADMIN_DEACTIVATION_ACTIVE_TASK_ERROR',
  DENIED: 'SKILL_LEARNING_ADMIN_DEACTIVATION_DENIED_ERROR',
  SAVE_ERROR: 'SKILL_LEARNING_ADMIN_DEACTIVATION_ERROR',
};

export function hasPublishedSkillLevelCount(value: number | null | undefined): value is number {
  return typeof value === 'number' && !isNaN(value) && value >= SKILL_LEARNING_LEVEL_COUNT.ONE;
}

export function normalizePublishedSkillLevelCount(value: number | null | undefined): number {
  if (value === SKILL_LEARNING_EMPTY_COUNT) {
    return SKILL_LEARNING_EMPTY_COUNT;
  }
  if (!hasPublishedSkillLevelCount(value)) {
    return SKILL_LEARNING_LEVEL_COUNT.THREE;
  }
  return Math.max(
    SKILL_LEARNING_LEVEL_COUNT.ONE,
    Math.min(SKILL_LEARNING_LEVEL_COUNT.THREE, value),
  );
}

export function normalizeConfiguredSkillLevelCount(value: number | null | undefined): number {
  if (!hasPublishedSkillLevelCount(value)) {
    return SKILL_LEARNING_LEVEL_COUNT.ONE;
  }
  return Math.min(SKILL_LEARNING_LEVEL_COUNT.THREE, value);
}

export function resolveSkillLevelType(
  level: number,
  level2Type: SkillLevelType | null | undefined,
  level3Type: SkillLevelType | null | undefined,
): SkillLevelType {
  if (level === SKILL_LEARNING_LEVEL_COUNT.ONE) {
    return SKILL_LEVEL_TYPE.TEST;
  }
  if (level === SKILL_LEARNING_LEVEL_COUNT.TWO) {
    return level2Type || SKILL_LEVEL_TYPE.TASK;
  }
  return level3Type || SKILL_LEVEL_TYPE.TASK;
}

export function isAllTestSkillStructure(
  count: number,
  level2Type: SkillLevelType | null | undefined,
  level3Type: SkillLevelType | null | undefined,
): boolean {
  if (count < SKILL_LEARNING_LEVEL_COUNT.TWO) {
    return true;
  }
  if (level2Type !== SKILL_LEVEL_TYPE.TEST) {
    return false;
  }
  return count < SKILL_LEARNING_LEVEL_COUNT.THREE || level3Type === SKILL_LEVEL_TYPE.TEST;
}

export const SKILL_PRACTICE_TASK_STATUS = {
  CREATED: 'CREATED' as SkillPracticeTaskStatus,
  IN_WORK: 'IN_WORK' as SkillPracticeTaskStatus,
  COMPLETED: 'COMPLETED' as SkillPracticeTaskStatus,
  CANCELLED: 'CANCELLED' as SkillPracticeTaskStatus,
};

export const SKILL_PRACTICE_STATUS_LABELS: { [status: string]: string } = {
  CREATED: 'SKILL_LEARNING_PRACTICE_STATUS_CREATED',
  IN_WORK: 'SKILL_LEARNING_PRACTICE_STATUS_IN_WORK',
  COMPLETED: 'SKILL_LEARNING_PRACTICE_STATUS_COMPLETED',
  CANCELLED: 'SKILL_LEARNING_PRACTICE_STATUS_CANCELLED',
};

export const SKILL_SCORE_STATUS = {
  OK: 'OK' as SkillScoreStatus,
  DEADLINE_SOON: 'DEADLINE_SOON' as SkillScoreStatus,
  PENALTY_ZONE: 'PENALTY_ZONE' as SkillScoreStatus,
};



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/options/skill-learning.option.ts